import { useState } from "react";
import DashboardWrapper from "../components/layout/DashboardWrapper";
import { FeedbackItem, useAppUpdates, useFeedbackHelpers, useFeedbackManagement } from "../hooks/useFeedbackData";
import { TrashIcon, EyeIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { safeFeedbackImageUrl } from "../utils/safeFeedbackImageUrl";

const FeedbackPage: React.FC = () => {
    const {
        feedbackList,
        deleteFeedback,
        loading: feedbackLoading,
    } = useFeedbackManagement();

    const { formatDate } = useFeedbackHelpers();

    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [feedbackToDelete, setFeedbackToDelete] = useState<FeedbackItem | null>(null);
    const [deleting, setDeleting] = useState(false);
    const [selectedFeedback, setSelectedFeedback] = useState<FeedbackItem | null>(null);

    const handleDeleteClick = (feedback: FeedbackItem) => {
        setFeedbackToDelete(feedback);
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (!feedbackToDelete) return;

        setDeleting(true);
        try {
            await deleteFeedback(feedbackToDelete.id);
            setDeleteDialogOpen(false);
            setFeedbackToDelete(null);
        } catch (error) {
            console.error("Error deleting feedback:", error);
        }
        setDeleting(false);
    };

    return (
        <DashboardWrapper>
            <div className="w-full flex flex-col gap-4 md:gap-6 p-4 md:p-6 md:h-[calc(100vh-64px)] overflow-hidden">
                <h1 className="text-xl md:text-2xl font-heading text-gray-400">Feedback Management (Deprecated)</h1>

                {/* Stats Row */}
                <div className="w-full grid grid-cols-2 gap-2 shrink-0">
                    <div className="w-full bg-white rounded-xl flex flex-col p-3 md:p-4 gap-1 md:gap-2 border">
                        <div className="text-xl md:text-2xl text-gray-400 font-bold">
                            {feedbackList.length}
                        </div>
                        <div className="text-[10px] md:text-xs text-gray-500 uppercase">Total Submissions</div>
                    </div>
                    <div className="w-full bg-white rounded-xl flex flex-col p-3 md:p-4 gap-1 md:gap-2 border">
                        <div className="text-xl md:text-2xl text-gray-400 font-bold">
                            {feedbackList.filter(f => f.adminResponse).length}
                        </div>
                        <div className="text-[10px] md:text-xs text-gray-500 uppercase">Responded</div>
                    </div>
                </div>

                {/* Feedback Table */}
                <div className="flex-1 min-h-0 bg-white rounded-xl border overflow-hidden flex flex-col">
                    {/* Table Header */}
                    <div className="grid grid-cols-[1fr_auto_auto] md:grid-cols-[1.5fr_2fr_4fr_1fr_1fr_auto] gap-2 md:gap-4 px-4 py-3 bg-gray-50 border-b text-xs font-medium text-gray-500 uppercase shrink-0">
                        <div className="hidden md:block">Date</div>
                        <div className="hidden md:block">User ID</div>
                        <div>Feedback</div>
                        <div className="hidden md:block">Image</div>
                        <div>Status</div>
                        <div className="hidden md:block">Actions</div>
                    </div>

                    {/* Table Body */}
                    <div className="flex-1 overflow-y-auto min-h-0">
                        {feedbackLoading ? (
                            <div className="flex items-center justify-center h-32 text-gray-400">
                                Loading feedback...
                            </div>
                        ) : feedbackList.length === 0 ? (
                            <div className="flex items-center justify-center h-32 text-gray-400">
                                No feedback submissions found
                            </div>
                        ) : (
                            feedbackList.map((item) => (
                                <div
                                    key={item.id}
                                    className="grid grid-cols-[1fr_auto_auto] md:grid-cols-[1.5fr_2fr_4fr_1fr_1fr_auto] gap-2 md:gap-4 px-4 py-3 border-b hover:bg-gray-50 items-center transition-colors cursor-pointer md:cursor-default"
                                    onClick={() => {
                                        // On mobile, clicking the row opens details
                                        if (window.innerWidth < 768) {
                                            setSelectedFeedback(item);
                                        }
                                    }}
                                >
                                    {/* Date - Desktop only */}
                                    <div className="hidden md:block">
                                        <span className="text-sm text-gray-600">
                                            {formatDate(item.createdAt)}
                                        </span>
                                    </div>

                                    {/* User ID - Desktop only */}
                                    <div className="hidden md:block">
                                        <span className="text-xs rounded truncate block">
                                            {item.userId}
                                        </span>
                                    </div>

                                    {/* Feedback */}
                                    <div className="flex flex-col gap-0.5 min-w-0">
                                        <span className="text-xs text-gray-400 md:hidden">
                                            {formatDate(item.createdAt)}
                                        </span>
                                        <p className="text-sm text-gray-600 line-clamp-2">{item.feedback}</p>
                                        {/* Image badge - Mobile only */}
                                        {item.imageUrl && (
                                            <span className="inline-block w-fit px-1.5 py-0.5 text-[10px] uppercase rounded-full bg-blue-100 text-blue-800 md:hidden mt-1">
                                                Has Image
                                            </span>
                                        )}
                                    </div>

                                    {/* Has Image - Desktop only */}
                                    <div className="hidden md:block">
                                        {item.imageUrl ? (
                                            <span className="inline-flex px-2 py-0.5 text-[10px] uppercase rounded-full bg-blue-100 text-blue-800">
                                                Yes
                                            </span>
                                        ) : (
                                            <span className="text-xs text-gray-400">—</span>
                                        )}
                                    </div>

                                    {/* Status */}
                                    <div className="flex items-center">
                                        <span className={`inline-flex px-2 py-0.5 text-[10px] uppercase rounded-full whitespace-nowrap ${
                                            item.adminResponse
                                                ? 'bg-green-100 text-green-800'
                                                : 'bg-orange-100 text-orange-800'
                                        }`}>
                                            {item.adminResponse ? 'Done' : 'Open'}
                                        </span>
                                    </div>

                                    {/* Actions */}
                                    <div className="flex items-center justify-end gap-1 md:gap-2">
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setSelectedFeedback(item);
                                            }}
                                            className="p-1.5 rounded bg-blue-100 hover:bg-blue-200 text-blue-700"
                                            title="View details"
                                        >
                                            <EyeIcon className="w-4 h-4" />
                                        </button>
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleDeleteClick(item);
                                            }}
                                            className="p-1.5 rounded bg-red-100 hover:bg-red-200 text-red-700"
                                            title="Delete feedback"
                                        >
                                            <TrashIcon className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>

                {/* View Details Modal */}
                {selectedFeedback && (
                    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:p-4 bg-black bg-opacity-50">
                        <div className="bg-white rounded-t-lg sm:rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                            <div className="flex justify-between items-center px-4 sm:px-6 py-4 border-b border-gray-200 sticky top-0 bg-white">
                                <h2 className="text-lg font-heading">Feedback Details</h2>
                                <button
                                    onClick={() => setSelectedFeedback(null)}
                                    className="p-2 hover:bg-gray-100 rounded-full"
                                >
                                    <XMarkIcon className="w-5 h-5" />
                                </button>
                            </div>

                            <div className="px-4 sm:px-6 py-4 space-y-4">
                                <div>
                                    <label className="block text-xs font-medium text-gray-500 uppercase mb-1">Date</label>
                                    <p className="text-sm">{formatDate(selectedFeedback.createdAt)}</p>
                                </div>

                                <div>
                                    <label className="block text-xs font-medium text-gray-500 uppercase mb-1">User ID</label>
                                    <code className="text-xs sm:text-sm bg-gray-100 px-2 py-1 rounded block overflow-x-auto">
                                        {selectedFeedback.userId}
                                    </code>
                                </div>

                                <div>
                                    <label className="block text-xs font-medium text-gray-500 uppercase mb-1">Feedback</label>
                                    <p className="text-sm whitespace-pre-line bg-gray-50 p-3 rounded-lg">
                                        {selectedFeedback.feedback}
                                    </p>
                                </div>

                                {safeFeedbackImageUrl(selectedFeedback.imageUrl) && (
                                    <div>
                                        <label className="block text-xs font-medium text-gray-500 uppercase mb-1">Attached Image</label>
                                        <img
                                            src={safeFeedbackImageUrl(selectedFeedback.imageUrl) ?? undefined}
                                            alt="Feedback screenshot"
                                            className="max-w-full max-h-[200px] sm:max-h-[300px] rounded-lg border cursor-pointer hover:opacity-90"
                                            onClick={() => {
                                                const url = safeFeedbackImageUrl(selectedFeedback.imageUrl);
                                                if (url) window.open(url, "_blank", "noopener,noreferrer");
                                            }}
                                        />
                                    </div>
                                )}

                                {selectedFeedback.adminResponse && (
                                    <div>
                                        <label className="block text-xs font-medium text-gray-500 uppercase mb-1">Admin Response</label>
                                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                                            <p className="text-sm text-blue-800 whitespace-pre-line">
                                                {selectedFeedback.adminResponse}
                                            </p>
                                            {selectedFeedback.adminResponseAt && (
                                                <p className="text-xs text-blue-600 mt-2">
                                                    Responded: {formatDate(selectedFeedback.adminResponseAt)}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="flex justify-end gap-2 px-4 sm:px-6 py-4 border-t">
                                <button
                                    onClick={() => setSelectedFeedback(null)}
                                    className="px-4 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Delete Confirmation Modal */}
                {deleteDialogOpen && feedbackToDelete && (
                    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:p-4 bg-black bg-opacity-50">
                        <div className="bg-white rounded-t-lg sm:rounded-lg shadow-xl max-w-md w-full">
                            <div className="px-4 sm:px-6 py-4 border-b border-gray-200">
                                <h2 className="text-lg font-heading">Delete Feedback</h2>
                            </div>
                            <div className="px-4 sm:px-6 py-4">
                                <p className="text-sm text-gray-600">
                                    Are you sure you want to delete this feedback? This action cannot be undone.
                                </p>
                            </div>
                            <div className="flex justify-between gap-3 px-4 sm:px-6 py-4 border-t border-gray-200">
                                <button
                                    onClick={() => {
                                        setDeleteDialogOpen(false);
                                        setFeedbackToDelete(null);
                                    }}
                                    className="px-4 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                                    disabled={deleting}
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleDeleteConfirm}
                                    disabled={deleting}
                                    className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
                                >
                                    {deleting ? "Deleting..." : "Delete"}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </DashboardWrapper>
    );
};

export default FeedbackPage;
